import { useMemo } from 'react'
import { useAgenciesList } from '~/api/queries/agency'
import { orderStatus } from '~/api/queries/order'
import { CompanyChipValue } from '~/components/Filters/FilterBar/chips/company'
import { QualificationChipValue } from '~/components/Filters/FilterBar/chips/qualification'
import { UsersChipValue } from '~/components/Filters/FilterBar/chips/users'
import type { FilterDefinition } from '~/components/Filters/FilterBar/types'
import { CompaniesAutocomplete } from '~/domains/companies/components/CompaniesAutocomplete'
import { QualificationsDropdown } from '~/domains/taxonomies/components/QualificationsDropdown'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'

const intensityOptions = [
  { value: 'Tres faible', label: 'Très faible' },
  { value: 'Faible', label: 'Faible' },
  { value: 'Forte', label: 'Forte' },
  { value: 'Tres forte', label: 'Très forte' }
]

export const useOrderFilterDefinitions = (): FilterDefinition[] => {
  const { data: agencies } = useAgenciesList('orders')
  const { data: nafs } = useTaxonomy('nafs')
  const { data: contractTerms } = useTaxonomy('contract_terms')
  const { data: segmentations } = useTaxonomy('segmentations')

  return useMemo(
    () => [
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        options: orderStatus.map((s) => ({
          label: s.name,
          value: String(s.id)
        })),
        emptyLabel: 'Tous'
      },
      {
        key: 'company',
        label: 'Entreprise',
        group: 'Entreprise',
        menuLabel: 'Nom',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <CompaniesAutocomplete
            key={value || 'empty'}
            inlineList
            ids={value ? [value] : []}
            small
            fullWidth
            placeholder="Rechercher une entreprise"
            emptyLabel="Toutes"
            onChange={(v) => {
              onChange(v)
              onClose()
            }}
          />
        ),
        renderValue: (value) => <CompanyChipValue id={value} />
      },
      {
        key: 'agency',
        label: 'Agences',
        type: 'select',
        options:
          agencies?.map((a) => ({ label: a.name, value: String(a.id) })) ?? [],
        searchable: true,
        multiSelect: true
      },
      {
        key: 'user',
        label: 'Utilisateurs',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <UsersAutocomplete
            key={value || 'empty'}
            multiple={true}
            inlineList
            ids={value ? value.split(',').filter(Boolean) : []}
            listingContext="orders"
            small
            fullWidth
            placeholder="Rechercher un utilisateur"
            onChange={(v) => {
              onChange(v.join(','))
              onClose()
            }}
          />
        ),
        renderValue: (value) => <UsersChipValue value={value} />
      },
      {
        key: 'naf',
        label: 'Code NAF',
        group: 'Entreprise',
        menuLabel: 'Code NAF',
        type: 'select',
        options: (nafs ?? []).map((n) => ({
          label: n.code ? `${n.code} – ${n.name}` : n.name,
          value: String(n.id)
        })),
        searchable: true,
        emptyLabel: 'Tous'
      },
      {
        key: 'qualification',
        label: 'Qualification',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <QualificationsDropdown
            key={value || 'empty'}
            inlineList
            small
            fullWidth
            emptyLabel="Toutes"
            placeholder="Rechercher une qualification"
            defaultValue={value || undefined}
            onChange={(v) => {
              onChange(v)
              onClose()
            }}
          />
        ),
        renderValue: (v) => <QualificationChipValue id={v} />
      },
      {
        key: 'contractType',
        label: 'Type de contrat',
        type: 'select',
        options: (contractTerms ?? []).map((c) => ({
          label: c.name,
          value: String(c.id)
        })),
        emptyLabel: 'Tous'
      },
      {
        key: 'feasibility',
        label: 'Faisabilité',
        type: 'select',
        options: intensityOptions,
        emptyLabel: 'Toutes'
      },
      {
        key: 'urgency',
        label: 'Urgence',
        type: 'select',
        options: intensityOptions,
        emptyLabel: 'Toutes'
      },
      {
        key: 'segmentation',
        label: 'Segmentation',
        group: 'Entreprise',
        menuLabel: 'Segmentation',
        type: 'select',
        options: (segmentations ?? []).map((s) => ({
          label: s.name,
          value: String(s.id)
        })),
        emptyLabel: 'Toutes'
      },
      {
        label: 'Créées',
        group: 'Dates',
        menuLabel: 'Date de création',
        type: 'dateRange',
        keyAfter: 'createdAfter',
        keyBefore: 'createdBefore'
      },
      {
        label: 'Modifiées',
        group: 'Dates',
        menuLabel: 'Date de modification',
        type: 'dateRange',
        keyAfter: 'modifiedAfter',
        keyBefore: 'modifiedBefore'
      },
      {
        label: 'Dernière activité',
        group: 'Dates',
        menuLabel: 'Date de dernière activité',
        type: 'dateRange',
        keyAfter: 'lastActivityDateAfter',
        keyBefore: 'lastActivityDateBefore'
      }
    ],
    [agencies, nafs, contractTerms, segmentations]
  )
}
